import React, { useContext, useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { styled } from "@mui/material/styles";
import LockOutlined from "@mui/icons-material/LockOutlined";
import { collection, onSnapshot, query, where } from "firebase/firestore";

import UserContext from "../assets/user_context";
import { useAuth } from "../services/use-auth";
import { useEntitlements } from "../services/useEntitlement";
import HelpButton from "../tour/HelpButton";

const TopNavWrapper = styled("div")(({ theme }) => ({
  position: "sticky",
  top: theme.spacing(1), // Small offset from viewport top to prevent badge cutoff
  display: "flex",
  flexDirection: "row",
  justifyContent: "flex-start",
  alignItems: "center",
  padding: theme.spacing(2),
  paddingLeft: "2vw",
  paddingRight: "5vw",
  zIndex: 1000, // Higher z-index to stay above other content
  backgroundColor: theme.palette.background.default,
  borderBottom: `1px solid ${theme.palette.divider}`,

  // Create a pseudo-element for the gradient border
  "&::after": {
    content: '""',
    display: "block",
    width: "100%",
    height: "2px",
    background:
      theme.palette.mode === "light"
        ? "linear-gradient(to left, rgba(207, 207, 207, 0), rgba(207, 207, 207, 1) 50%, rgba(207, 207, 207, 0))"
        : `linear-gradient(to left, transparent, ${theme.palette.divider} 50%, transparent)`,
    position: "absolute",
    bottom: 0,
    left: 0,
  },
}));

const PillNavGroup = styled("div")(({ theme }) => ({
  position: "relative",
  display: "flex",
  backgroundColor:
    theme.palette.mode === "light"
      ? theme.palette.grey[100]
      : theme.palette.grey[200],
  borderRadius: "50px",
  padding: "2px",
  border: `1px solid ${
    theme.palette.mode === "light"
      ? theme.palette.grey[300]
      : theme.palette.grey[400]
  }`,
  // Scroll the pill row horizontally when the tabs exceed the available width
  // (e.g. the 6-tab Reports nav at ≤1080px) instead of clipping the last pill.
  // overflowY stays hidden so the sliding background keeps the rounded shape;
  // the scrollbar is hidden so the pill looks unchanged when everything fits.
  overflowX: "auto",
  overflowY: "hidden",
  scrollbarWidth: "none",
  "&::-webkit-scrollbar": { display: "none" },
}));

const SlidingBackground = styled("div")(({ theme, width, translateX }) => ({
  position: "absolute",
  top: "2px",
  bottom: "2px",
  left: "2px",
  width: `${width}px`,
  backgroundColor:
    theme.palette.mode === "light"
      ? theme.palette.grey[900]
      : theme.palette.primary.main,
  borderRadius: "48px",
  transform: `translateX(${translateX}px)`,
  transition: "transform 0.3s ease, width 0.3s ease",
  zIndex: 1,
}));

const StyledNavLinkLikeButton = styled("p")(({ theme, isActive }) => ({
  position: "relative",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: theme.typography.subtitle1.fontFamily,
  color: isActive
    ? theme.palette.mode === "light"
      ? theme.palette.common.white
      : theme.palette.grey[900]
    : theme.palette.mode === "light"
      ? theme.palette.text.primary
      : theme.palette.text.primary,
  fontSize: "1.1rem",
  fontWeight: 500,
  padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
  textDecoration: "none",
  margin: 0,
  cursor: "pointer",
  transition: "color 0.3s ease",
  zIndex: 2,
  whiteSpace: "nowrap",
  minWidth: "fit-content",

  [theme.breakpoints.down(1500)]: {
    fontSize: "1rem",
    padding: `${theme.spacing(0.8)} ${theme.spacing(1.8)}`,
  },
  [theme.breakpoints.down(1300)]: {
    fontSize: "0.9rem",
    padding: `${theme.spacing(0.7)} ${theme.spacing(1.5)}`,
  },
  [theme.breakpoints.down(1100)]: {
    fontSize: "0.8rem",
    padding: `${theme.spacing(0.6)} ${theme.spacing(1.2)}`,
  },

  "&:hover": {
    color: isActive
      ? theme.palette.mode === "light"
        ? theme.palette.common.white
        : theme.palette.grey[900]
      : theme.palette.mode === "light"
        ? theme.palette.text.secondary
        : theme.palette.text.secondary,
  },
}));

const StyledNavLink = styled(NavLink)(({ theme, isActive }) => ({
  position: "relative",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: theme.typography.subtitle1.fontFamily,
  color: isActive
    ? theme.palette.mode === "light"
      ? theme.palette.common.white
      : theme.palette.grey[900]
    : theme.palette.mode === "light"
      ? theme.palette.text.primary
      : theme.palette.text.primary,
  fontSize: "1.1rem",
  fontWeight: 500,
  padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
  textDecoration: "none",
  cursor: "pointer",
  transition: "color 0.3s ease",
  zIndex: 2,
  whiteSpace: "nowrap",
  minWidth: "fit-content",

  [theme.breakpoints.down(1500)]: {
    fontSize: "1rem",
    padding: `${theme.spacing(0.8)} ${theme.spacing(1.8)}`,
  },
  [theme.breakpoints.down(1300)]: {
    fontSize: "0.9rem",
    padding: `${theme.spacing(0.7)} ${theme.spacing(1.5)}`,
  },
  [theme.breakpoints.down(1100)]: {
    fontSize: "0.8rem",
    padding: `${theme.spacing(0.6)} ${theme.spacing(1.2)}`,
  },

  "&:hover": {
    textDecoration: "none",
    color: isActive
      ? theme.palette.mode === "light"
        ? theme.palette.common.white
        : theme.palette.grey[900]
      : theme.palette.mode === "light"
        ? theme.palette.text.secondary
        : theme.palette.text.secondary,
  },
}));

// Locked features stay visible and navigable — the destination page renders
// the upsell. Treatment: dimmed pill + small lock glyph before the label.
const LockGlyph = () => (
  <LockOutlined sx={{ fontSize: 13, marginRight: "5px" }} />
);

const lockedPillProps = (feature) => ({
  style: { opacity: 0.55 },
  "data-testid": `nav-pill-${feature}-locked`,
});

const TopNav = ({ collapsed }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    user,
    userData,
    orgData,
    orgRoles,
    subscriptionData,
  } = useContext(UserContext);

  // Effective entitlements (plan + admin overrides) drive nav visibility —
  // replaces the legacy features.* flags from context.
  const entitlements = useEntitlements();
  const { db } = useAuth();

  // Multi-Subscribe only surfaces for users who actually have a multi-org
  // subscription — everyone else opts in from the billing page. Live query so
  // the item appears the moment they convert.
  const [hasMultiSubscription, setHasMultiSubscription] = useState(false);
  useEffect(() => {
    if (!db || !user?.uid) return undefined;
    const multiQuery = query(
      collection(db, "stripeUsers"),
      where("userId", "==", user.uid),
      where("subscriptionType", "==", "multi"),
    );
    return onSnapshot(
      multiQuery,
      (snap) => setHasMultiSubscription(!snap.empty),
      () => setHasMultiSubscription(false),
    );
  }, [db, user?.uid]);

  // Managed (multi-sub) orgs have no per-org billing to manage: the firm's
  // billing owner gets routed to the firm console; everyone else loses the
  // item (nothing actionable on /account/billing for a managed org).
  const isManagedBilling = subscriptionData?.billingType === "managed";
  const isBillingOwner =
    isManagedBilling && subscriptionData?.billingUserId === user?.uid;

  // Hide Multi-Subscribe when the Subscription item already routes this user
  // to the firm console — two items to the same page reads as a bug.
  const showMultiSubscribe =
    hasMultiSubscription &&
    !(isManagedBilling && isBillingOwner && orgRoles?.includes("subscriber"));

  // Check if current org is a child org and user is owner
  const isChildOrg = orgData?.orgHierarchy?.isChild || false;
  const isOwner = orgRoles?.includes("owner");
  const showConsolidation = isChildOrg && isOwner;


  const navGroupRef = useRef(null);
  const [activeIndicator, setActiveIndicator] = useState({
    width: 0,
    translateX: 0,
  });

  const updateActiveIndicator = () => {
    if (navGroupRef.current) {
      const activeElement =
        navGroupRef.current.querySelector(".active") ||
        navGroupRef.current.querySelector('[data-active="true"]');
      if (activeElement) {
        const containerRect = navGroupRef.current.getBoundingClientRect();
        const activeRect = activeElement.getBoundingClientRect();
        setActiveIndicator({
          width: activeRect.width,
          translateX: activeRect.left - containerRect.left - 2, // Subtract padding (reduced from 4px to 2px)
        });
      }
    }
  };

  useEffect(() => {
    updateActiveIndicator();
    // Add a small delay to ensure the DOM has updated
    const timer = setTimeout(updateActiveIndicator, 50);
    return () => clearTimeout(timer);
    // entitlements: lock glyphs toggling changes pill widths without a
    // pathname change (e.g. subscription data resolving after mount)
  }, [location.pathname, entitlements]);

  useEffect(() => {
    const handleResize = () => updateActiveIndicator();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const renderNavGroup = (children) => (
    <PillNavGroup ref={navGroupRef}>
      <SlidingBackground
        width={activeIndicator.width}
        translateX={activeIndicator.translateX}
      />
      {children}
    </PillNavGroup>
  );

  return (
    <TopNavWrapper collapsed={collapsed} data-tour="nav-top-pills">
      <HelpButton />
      {location.pathname.includes("foundation") &&
        renderNavGroup([
          <StyledNavLink
            key="accounts"
            to="/foundation/chart-of-accounts"
            isActive={location.pathname.includes("chart-of-accounts")}>
            Accounts
          </StyledNavLink>,
          <StyledNavLink
            key="funds"
            to="/foundation/funds-list"
            isActive={location.pathname.includes("funds-list")}>
            Funds
          </StyledNavLink>,
          <StyledNavLink
            key="labels"
            to="/foundation/labels-list"
            isActive={location.pathname.includes("labels-list")}
            {...(entitlements.labels ? {} : lockedPillProps("labels"))}>
            {!entitlements.labels && <LockGlyph />}
            Labels
          </StyledNavLink>,
          <StyledNavLink
            key="budgets"
            to="/foundation/budgets"
            isActive={location.pathname.includes("budgets")}>
            Budgets
          </StyledNavLink>,
          <StyledNavLink
            key="donations"
            to="/foundation/donations"
            isActive={location.pathname.includes("donations") && !location.pathname.includes("integrations")}
            {...(entitlements.donorManagement
              ? {}
              : lockedPillProps("donations"))}>
            {!entitlements.donorManagement && <LockGlyph />}
            Donations
          </StyledNavLink>,
          <StyledNavLink
            key="integrations"
            to="/foundation/integrations"
            isActive={location.pathname.includes("integrations")}>
            Integrations
          </StyledNavLink>,
          <StyledNavLink
            key="payroll"
            to="/foundation/payroll"
            isActive={location.pathname.includes("payroll")}
            {...(entitlements.payroll ? {} : lockedPillProps("payroll"))}>
            {!entitlements.payroll && <LockGlyph />}
            Payroll
          </StyledNavLink>,
          ...(showConsolidation
            ? [
                <StyledNavLink
                  key="consolidation"
                  to="/foundation/consolidation"
                  isActive={location.pathname.includes("consolidation")}>
                  Consolidation
                </StyledNavLink>,
              ]
            : []),
        ])}

      {location.pathname.includes("upkeep") && !location.pathname.includes("upkeep/ttc") &&
        renderNavGroup([
          <StyledNavLink
            key="banks"
            to="/upkeep/bank-accounts"
            isActive={location.pathname.includes("bank-accounts")}>
            Banks
          </StyledNavLink>,
          <StyledNavLink
            key="credit-cards"
            to="/upkeep/credit-cards"
            isActive={location.pathname.includes("credit-cards")}>
            Credit Cards
          </StyledNavLink>,
          <StyledNavLink
            key="reconciliation"
            to="/upkeep/reconciliation"
            isActive={location.pathname.includes("reconciliation")}>
            Reconciliation
          </StyledNavLink>,
          <StyledNavLink
            key="donations"
            to="/upkeep/donations"
            isActive={location.pathname.includes("donations")}
            {...(entitlements.donorManagement
              ? {}
              : lockedPillProps("donations"))}>
            {!entitlements.donorManagement && <LockGlyph />}
            Donations
          </StyledNavLink>,
          <StyledNavLink
            key="journal-entry"
            to="/upkeep/journal-entry"
            isActive={location.pathname.includes("journal-entry")}>
            Journal Entries
          </StyledNavLink>,
          // Payables & Receivables (accrual accounting). Always rendered:
          // entitled-but-not-enabled orgs land on the enable gate, and
          // non-entitled orgs get the locked pill → upsell (like labels /
          // payroll / donations). Hiding it would strand non-entitled orgs
          // with no door to the upsell.
          <StyledNavLink
            key="payables-receivables"
            to="/upkeep/payables-receivables"
            isActive={location.pathname.includes("payables-receivables")}
            {...(entitlements.accrualAccounting
              ? {}
              : lockedPillProps("accrualAccounting"))}>
            {!entitlements.accrualAccounting && <LockGlyph />}
            Payables &amp; Receivables
          </StyledNavLink>,
        ])}

      {location.pathname.includes("reports") &&
        renderNavGroup([
          <StyledNavLink
            key="reports-library"
            to="/reports/library"
            isActive={location.pathname.includes("/reports/library")}>
            Reports Library
          </StyledNavLink>,
          <StyledNavLink
            key="balance-sheet"
            to="/reports/balance-sheet"
            isActive={location.pathname.includes("balance-sheet")}>
            Balance Sheet
          </StyledNavLink>,
          <StyledNavLink
            key="income-statement"
            to="/reports/income-statement"
            isActive={location.pathname.includes("income-statement")}>
            Income Statement
          </StyledNavLink>,
          <StyledNavLink
            key="general-ledger"
            to="/reports/general-ledger"
            isActive={location.pathname.includes("general-ledger")}>
            General Ledger
          </StyledNavLink>,
          <StyledNavLink
            key="transactions-list"
            to="/reports/transactions-list"
            isActive={location.pathname.includes("transactions-list")}>
            Transactions List
          </StyledNavLink>,
          <StyledNavLink
            key="reconciliation-reports"
            to="/reports/reconciliation"
            isActive={location.pathname.includes("reconciliation")}>
            Reconciliation
          </StyledNavLink>,
          <StyledNavLink
            key="donations-reports"
            to="/reports/donations"
            isActive={location.pathname.includes("donations")}
            {...(entitlements.donorManagement
              ? {}
              : lockedPillProps("donations"))}>
            {!entitlements.donorManagement && <LockGlyph />}
            Donations
          </StyledNavLink>,
        ])}

      {location.pathname.split("/")[1].includes("account") &&
        renderNavGroup([
          <StyledNavLink
            key="settings"
            to="/account/user-management"
            isActive={location.pathname.includes("user-management")}>
            User Management
          </StyledNavLink>,
          <StyledNavLink
            key="org-management"
            to="/account/org-management"
            isActive={location.pathname.includes("org-management")}>
            Org Management
          </StyledNavLink>,
          ...(orgRoles?.length > 0 &&
          orgRoles.some((role) => role === "subscriber") &&
          (!isManagedBilling || isBillingOwner)
            ? [
                <StyledNavLinkLikeButton
                  key="subscription"
                  isActive={
                    location.pathname.includes("/account/billing") ||
                    location.pathname.includes("/account/subscriptions")
                  }
                  data-active={
                    location.pathname.includes("/account/billing") ||
                    location.pathname.includes("/account/subscriptions")
                      ? "true"
                      : "false"
                  }
                  onClick={() =>
                    navigate(
                      isManagedBilling
                        ? "/account/subscriptions"
                        : "/account/billing",
                    )
                  }>
                  Subscription
                </StyledNavLinkLikeButton>,
              ]
            : []),
          ...(showMultiSubscribe
            ? [
                <StyledNavLink
                  key="multi-subscribe"
                  to="/account/subscriptions"
                  isActive={location.pathname.includes("subscriptions")}>
                  Multi-Subscribe
                </StyledNavLink>,
              ]
            : []),
        ])}

      {location.pathname.split("/")[1].includes("contacts") &&
        renderNavGroup([
          <StyledNavLink
            key="contacts"
            to="/contacts/contacts"
            isActive={location.pathname.includes("/contacts/contacts")}>
            Contacts
          </StyledNavLink>,
          <StyledNavLink
            key="donors"
            to="/contacts/donors"
            isActive={location.pathname.includes("donors")}
            {...(entitlements.donorManagement
              ? {}
              : lockedPillProps("donors"))}>
            {!entitlements.donorManagement && <LockGlyph />}
            Donors
          </StyledNavLink>,
        ])}

      {location.pathname.includes("admin") &&
        renderNavGroup([
          <StyledNavLink
            key="admin-t3"
            to="/admin-t3"
            isActive={location.pathname.includes("admin-t3")}>
            T3 Admin
          </StyledNavLink>,
          ...(userData?.superAdmin ? [<StyledNavLink
            key="super-admin"
            to="/super-admin"
            isActive={location.pathname.includes("super-admin")}>
            Super Admin
          </StyledNavLink>] : []),
          <StyledNavLink
            key="admin-management"
            to="/admin-management"
            isActive={location.pathname.includes("admin-management")}>
            Team Management
          </StyledNavLink>,
          <StyledNavLink
            key="admin-unit-tests"
            to="/admin-unit-tests"
            isActive={location.pathname.includes("admin-unit-tests")}>
            Unit Tests
          </StyledNavLink>,
          <StyledNavLink
            key="admin-conversion"
            to="/admin-conversion"
            isActive={location.pathname.includes("admin-conversion")}>
            Conversion
          </StyledNavLink>,
          <StyledNavLink
            key="admin-bookkeeping"
            to="/admin-bookkeeping"
            isActive={location.pathname.includes("admin-bookkeeping")}>
            Bookkeeping
          </StyledNavLink>,
        ])}


    </TopNavWrapper>
  );
};

export default TopNav;
